import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import {
  optionalNumberSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoManwayJSON {
  type: GeckoFeatureModelType.Manway
  name: string
  feature_geometry: {
    x: number
    y: number
    radius: number
    repad_diameter?: number
  }
  surface_specs: {
    thickness: number | undefined
  }
}

export const NewGeckoManwayJSON: GeckoManwayJSON = {
  type: GeckoFeatureModelType.Manway,
  name: 'New Manway',
  feature_geometry: {
    x: 0,
    y: 0,
    radius: 0,
    repad_diameter: undefined,
  },
  surface_specs: {
    thickness: undefined,
  },
}

export class GeckoManway extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.Manway
  readonly geometry: GeckoComponentCoords
  readonly radius: GeckoMeasurement
  readonly repadDiameter: GeckoMeasurement | undefined
  readonly thickness: GeckoMeasurement | undefined

  private constructor(
    id: string,
    name: string,
    geometry: GeckoComponentCoords,
    radius: GeckoMeasurement,
    repadDiameter: GeckoMeasurement | undefined,
    thickness: GeckoMeasurement | undefined
  ) {
    super(id)
    this.name = name
    this.geometry = geometry
    this.radius = radius
    this.repadDiameter = repadDiameter
    this.thickness = thickness
  }

  public static CreateFromJSON(
    componentID: string,
    manwayID: string,
    manwayJSON: GeckoManwayJSON
  ): GeckoManway {
    const featureGeometryData = manwayJSON.feature_geometry as Record<
      string,
      number
    >
    const manwayGeometry = GeckoComponentCoords.CreateFromValues(
      componentID,
      featureGeometryData.x,
      GeckoMeasurementUnit.Meters,
      featureGeometryData.y,
      GeckoMeasurementUnit.Meters
    )
    const manwayRadius = GeckoMeasurement.CreateFromValue(
      featureGeometryData.radius as number,
      GeckoMeasurementUnit.Meters
    )
    const manwayRepadDiameter = manwayJSON.feature_geometry.repad_diameter !== undefined
      ? GeckoMeasurement.CreateFromValue(
          manwayJSON.feature_geometry.repad_diameter as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined

    const surfaceSpecs = manwayJSON.surface_specs as Record<string, number>
    const manwayThickness =
      surfaceSpecs.thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.thickness as number,
            GeckoMeasurementUnit.Meters
          )
        : undefined

    const manwayName = manwayJSON.name as string

    return new GeckoManway(
      manwayID,
      manwayName,
      manwayGeometry,
      manwayRadius,
      manwayRepadDiameter,
      manwayThickness
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoManwayJSON>
  ): Map<string, GeckoManway> {
    return Object.keys(recordsJSON).reduce((resultObj, manwayID) => {
      resultObj.set(
        manwayID,
        GeckoManway.CreateFromJSON(componentID, manwayID, recordsJSON[manwayID])
      )
      return resultObj
    }, new Map<string, GeckoManway>())
  }

  asJSON(): GeckoManwayJSON {
    return generateManway(
      this.name,
      this.geometry.x,
      this.geometry.y,
      this.radius,
      this.repadDiameter,
      this.thickness
    )
  }
}

export function generateManway(
  name: string,
  x: GeckoMeasurement,
  y: GeckoMeasurement,
  radius: GeckoMeasurement,
  repadDiameter: GeckoMeasurement | undefined,
  thickness: GeckoMeasurement | undefined
): GeckoManwayJSON {
  const manwayJSON: GeckoManwayJSON = {
    type: GeckoFeatureModelType.Manway,
    name: name,
    feature_geometry: {
      x: x.toNumber(GeckoMeasurementUnit.Meters),
      y: y.toNumber(GeckoMeasurementUnit.Meters),
      radius: radius.toNumber(GeckoMeasurementUnit.Meters),
      repad_diameter: repadDiameter?.toNumber(GeckoMeasurementUnit.Meters),
    },
    surface_specs: {
      thickness:
        thickness !== undefined
          ? thickness.toNumber(GeckoMeasurementUnit.Meters)
          : undefined,
    },
  }

  return manwayJSON
}

export const ManwayFormFields = (manwayJSON: GeckoManwayJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Manway Name',
      value: manwayJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: manwayJSON.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: manwayJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'radius',
      label: 'Diameter',
      value: manwayJSON.feature_geometry.radius * 2,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'repad_diameter',
      label: 'Repad Diameter',
      value: manwayJSON.feature_geometry.repad_diameter ? manwayJSON.feature_geometry.repad_diameter * 2 : undefined,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'thickness',
      label: 'Thickness',
      value: manwayJSON.surface_specs.thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const ManwayJSONFromFormFields = (formFields: FormField[]) => {
  const manwayThickness = getFormFieldByDataKey(formFields, 'thickness')
  return generateManway(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      (getFormFieldByDataKey(formFields, 'radius')?.value as number) / 2,
      GeckoMeasurementUnit.Meters
    ),
    getFormFieldByDataKey(formFields, 'repad_diameter')?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          (getFormFieldByDataKey(formFields, 'repad_diameter')?.value as number),
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    manwayThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          manwayThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined
  )
}

export const ManwayFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.Manway,
  title: 'Manways',
  singularName: 'Manway',
  fields: ['x', 'y', 'radius', 'repad_diameter', 'thickness'],
}
